import { createRootRouteWithContext, Link, Outlet } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Settings, Telescope, Users } from 'lucide-react';
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationsBell } from "@/components/NotificationsBell";
import { UserMenu } from '@/components/UserMenu';

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
});

function RootLayout() {
  return (
    // overflow-x-hidden — single universal guard against any nested
    // fixed-width element causing the whole page to scroll horizontally on
    // narrow viewports (the inbox panels and the iframe message body each
    // have their own internal scroll containers).
    <div className="flex h-dvh flex-col overflow-x-hidden bg-background text-foreground safe-pl safe-pr safe-pb">
      {/*
        On iOS we ask for apple-mobile-web-app-status-bar-style=black-translucent
        which lets the Dynamic Island sit ON TOP of our header. safe-pt drops
        env(safe-area-inset-top) of padding above the header's content so the
        clock + island never overlap the brand / nav row, while the header's
        own background still extends right up to the top of the screen.
      */}
      <header className="shrink-0 border-b border-border bg-card/50 backdrop-blur safe-pt">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-4 py-3 md:gap-4 md:px-6">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2 text-base font-semibold tracking-tight md:text-lg"
            aria-label="Wharf Mail home"
          >
            <img
              src="/icon-192.png"
              alt=""
              width={28}
              height={28}
              className="size-7 shrink-0 rounded-md"
            />
            <span>Wharf Mail</span>
          </Link>
          <nav className="flex items-center gap-2 text-sm text-muted-foreground md:gap-3">
            <Link
              to="/inbox"
              className="rounded-md px-2 py-1 hover:bg-accent/50 hover:text-foreground"
              activeProps={{ className: 'bg-accent text-foreground' }}
            >
              Inbox
            </Link>
            <Link
              to="/contacts"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 hover:bg-accent/50 hover:text-foreground"
              activeProps={{ className: 'bg-accent text-foreground' }}
              aria-label="Contacts"
            >
              <Users className="size-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">Contacts</span>
            </Link>
            <Link
              to="/research"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 hover:bg-accent/50 hover:text-foreground"
              activeProps={{ className: 'bg-accent text-foreground' }}
              aria-label="Research"
            >
              <Telescope className="size-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">Research</span>
            </Link>
            <Link
              to="/settings"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 hover:bg-accent/50 hover:text-foreground"
              activeProps={{ className: 'bg-accent text-foreground' }}
              aria-label="Settings"
            >
              <Settings className="size-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">Settings</span>
            </Link>
            <NotificationsBell />
            <ThemeToggle />
            <UserMenu />
          </nav>
        </div>
      </header>
      <main className="min-h-0 flex-1">
        <Outlet />
      </main>
    </div>
  );
}
